import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send } from "lucide-react";
import { toast } from "sonner";

import { api, getErrorMessage } from "@/api";

export default function MessagesPage({ user }) {
  const [searchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const requestedContactId = searchParams.get("with");

  useEffect(() => {
    const loadContacts = async () => {
      if (user.role === "manager") {
        const { data } = await api.get("/freelancers");
        const nextContacts = data.map((person) => ({ id: person.id, name: person.name }));
        setContacts(nextContacts);
        setActiveContact(nextContacts.find((person) => person.id === requestedContactId) || null);
      } else {
        const { data } = await api.get("/jobs");
        const unique = [...new Map(data.map((job) => [job.manager_id, job.manager_name])).entries()];
        const nextContacts = unique.map(([id, name]) => ({ id, name }));
        setContacts(nextContacts);
        setActiveContact(nextContacts.find((person) => person.id === requestedContactId) || null);
      }
    };
    loadContacts();
  }, [user.role, requestedContactId]);

  useEffect(() => {
    const loadMessages = async () => {
      if (!activeContact) return;
      const { data } = await api.get("/messages", { params: { counterpart_id: activeContact.id } });
      setMessages(data);
    };
    loadMessages();
  }, [activeContact]);

  const activeName = useMemo(() => activeContact?.name || "Select a contact", [activeContact]);

  const sendMessage = async (event) => {
    event.preventDefault();
    if (!activeContact) return;
    try {
      const { data } = await api.post("/messages", { recipient_id: activeContact.id, body: draft });
      setMessages([...messages, data]);
      setDraft("");
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <main className="messages-page" data-testid="messages-page">
      <header className="messages-heading">
        <p className="eyebrow">CONVERSATIONS</p>
        <h1>Keep the details<br /><em>in one place.</em></h1>
      </header>
      <section className="inbox" data-testid="messages-inbox">
        <aside className="contact-list" data-testid="contact-list">
          {contacts.map((contact) => <button className={activeContact?.id === contact.id ? "active" : ""} data-testid={`contact-${contact.id}`} key={contact.id} onClick={() => setActiveContact(contact)}><span>{contact.name.slice(0, 1)}</span>{contact.name}</button>)}
        </aside>
        <div className="message-thread" data-testid="message-thread">
          <div className="thread-name" data-testid="active-contact-name">{activeName}</div>
          <div className="thread-messages" data-testid="thread-messages">
            {!activeContact && <p className="thread-empty" data-testid="empty-thread-message">
              Choose someone from the crew to start a conversation.
            </p>}
            {messages.map((message) => (
              <div
                className={message.sender_id === user.id ? "message mine" : "message"}
                data-testid={`message-${message.id}`}
                key={message.id}
              >
                {message.body}
              </div>
            ))}
          </div>
          <form className="message-form" data-testid="message-form" onSubmit={sendMessage}>
            <input data-testid="message-input" disabled={!activeContact} onChange={(e) => setDraft(e.target.value)} placeholder="Write a message" required value={draft} />
            <button data-testid="send-message-button" disabled={!activeContact}><Send size={18} /></button>
          </form>
        </div>
      </section>
    </main>
  );
}